import { MotionValue, useScroll, useSpring, useTransform } from 'motion/react'
import { RefObject, useEffect, useState } from 'react'

type ParallaxParams = {
  ratioX?: number
  ratioY?: number
  ref: RefObject<HTMLElement | null>
}
type ParallaxTransform = { x?: MotionValue<number>; y?: MotionValue<number> }
type ParallaxHandler = (params: ParallaxParams) => {
  transform: ParallaxTransform
}

const calculateOffsets = (
  ref: RefObject<HTMLElement | null>,
  ratioX = 0,
  ratioY = 0.15
): [number, number] => {
  if (ref.current === null) return [0, 0]
  return [ref.current.offsetWidth * ratioX, ref.current.offsetHeight * ratioY]
}

export const useParallaxSlide: ParallaxHandler = ({
  ratioX = 0,
  ratioY = 0.15,
  ref,
}: ParallaxParams) => {
  const [offset, setOffset] = useState([0, 0])
  // Start with undefined so useScroll tracks window (safe, no throw).
  // Switch to the real ref once the element is mounted and in the DOM.
  // Passing { current: null } would throw "ref not hydrated" in motion.
  const [scrollTarget, setScrollTarget] = useState<
    RefObject<HTMLElement | null> | undefined
  >(undefined)

  useEffect(() => {
    if (ref.current) setScrollTarget(ref)
  }, [ref])

  const { scrollYProgress } = useScroll({
    target: scrollTarget,
    offset: ['start end', 'end start'],
  })

  const transform = {
    x: useTransform(scrollYProgress, [0, 1], [-offset[0], offset[0]]),
    y: useTransform(scrollYProgress, [0, 1], [-offset[1], offset[1]]),
  }

  useEffect(() => {
    if (ref.current === null) return
    setOffset(calculateOffsets(ref, ratioX, ratioY))
  }, [ratioX, ratioY, ref])

  return { transform }
}

export const useParallaxScale: ParallaxHandler = ({
  ratioX = 0.005,
  ratioY = 0.0005,
  ref,
}) => {
  const [offset, setOffset] = useState([0, 0])
  const [scrollTarget, setScrollTarget] = useState<
    RefObject<HTMLElement | null> | undefined
  >(undefined)

  useEffect(() => {
    if (ref.current) setScrollTarget(ref)
  }, [ref])

  const { scrollYProgress } = useScroll({
    target: scrollTarget,
    offset: ['start end', 'end start'],
  })

  const spring = useSpring(scrollYProgress, { stiffness: 1000, damping: 90 })
  const transform = {
    x: useTransform(spring, [0, 1], [1, offset[0]]),
    y: useTransform(spring, [0, 1], [1, 1 - offset[1]]),
  }

  useEffect(() => {
    if (ref.current === null) return
    setOffset(calculateOffsets(ref, ratioX, ratioY))
  }, [ratioX, ratioY, ref])

  return { transform }
}
